import { toApiStatus, toStatus } from '@/api/mappers/mapStatus';
import {
  EffectivePortfolio as ApiEffectivePortfolio,
  EffectivePortfolioSource as ApiEffectivePortfolioSource,
  ProductList as ApiProductList,
  ProductListCreateRequest as ApiProductListCreateRequest,
  ProductListListResponse as ApiProductListListResponse,
  ProductListRetailerAssignment as ApiProductListRetailerAssignment,
  ProductListRetailerAssignmentListResponse as ApiProductListRetailerAssignmentListResponse,
  ProductListSkuIdListResponse as ApiProductListSkuIdListResponse,
  ProductListStoreAssignment as ApiProductListStoreAssignment,
  ProductListStoreAssignmentListResponse as ApiProductListStoreAssignmentListResponse,
  ProductListUpdateRequest as ApiProductListUpdateRequest,
} from '@/gen/api';
import {
  EffectivePortfolio,
  EffectivePortfolioSource,
  ProductList,
  ProductListCreate,
  ProductListRetailerAssignment,
  ProductListRetailerAssignments,
  ProductLists,
  ProductListSkuIds,
  ProductListStoreAssignment,
  ProductListStoreAssignments,
  ProductListUpdate,
} from '@/models/ProductList';

export const toProductList = (productList: ApiProductList): ProductList => ({
  id: productList.id,
  code: productList.code,
  name: productList.name,
  description: productList.description,
  externalCode: productList.externalCode,
  supplierId: productList.supplierId,
  status: toStatus(productList.status),
  createdAt: productList.createdAt,
  updatedAt: productList.updatedAt,
});

export const toProductLists = (response: ApiProductListListResponse): ProductLists => ({
  items: response.items.map(toProductList),
  totalCount: response.totalCount,
  estimatedTotalCount: response.estimatedTotalCount,
});

export const toCreateProductListRequest = (
  productList: ProductListCreate,
): ApiProductListCreateRequest => ({
  name: productList.name,
  description: productList.description,
  externalCode: productList.externalCode,
  supplierId: productList.supplierId,
  status: toApiStatus(productList.status),
});

export const toUpdateProductListRequest = (
  productList: ProductListUpdate,
): ApiProductListUpdateRequest => ({
  name: productList.name,
  description: productList.description,
  externalCode: productList.externalCode,
  status: toApiStatus(productList.status),
});

export const toProductListSkuIds = (
  response: ApiProductListSkuIdListResponse,
): ProductListSkuIds => ({
  items: response.items,
  totalCount: response.totalCount,
  estimatedTotalCount: response.estimatedTotalCount,
});

export const toProductListRetailerAssignment = (
  assignment: ApiProductListRetailerAssignment,
): ProductListRetailerAssignment => ({
  retailerId: assignment.retailerId,
  status: toStatus(assignment.status),
  createdAt: assignment.createdAt,
  updatedAt: assignment.updatedAt,
});

export const toProductListRetailerAssignments = (
  response: ApiProductListRetailerAssignmentListResponse,
): ProductListRetailerAssignments => ({
  items: response.items.map(toProductListRetailerAssignment),
  totalCount: response.totalCount,
  estimatedTotalCount: response.estimatedTotalCount,
});

export const toProductListStoreAssignment = (
  assignment: ApiProductListStoreAssignment,
): ProductListStoreAssignment => ({
  storeId: assignment.storeId,
  status: toStatus(assignment.status),
  createdAt: assignment.createdAt,
  updatedAt: assignment.updatedAt,
});

export const toProductListStoreAssignments = (
  response: ApiProductListStoreAssignmentListResponse,
): ProductListStoreAssignments => ({
  items: response.items.map(toProductListStoreAssignment),
  totalCount: response.totalCount,
  estimatedTotalCount: response.estimatedTotalCount,
});

const toEffectivePortfolioSource = (
  source: ApiEffectivePortfolioSource,
): EffectivePortfolioSource => {
  switch (source) {
    case ApiEffectivePortfolioSource.StoreAssignment:
      return EffectivePortfolioSource.StoreAssignment;
    case ApiEffectivePortfolioSource.RetailerAssignment:
      return EffectivePortfolioSource.RetailerAssignment;
    case ApiEffectivePortfolioSource.Unrestricted:
      return EffectivePortfolioSource.Unrestricted;
    case ApiEffectivePortfolioSource.SupplierUnavailable:
      return EffectivePortfolioSource.SupplierUnavailable;
    default:
      return EffectivePortfolioSource.Unknown;
  }
};

export const toEffectivePortfolio = (portfolio: ApiEffectivePortfolio): EffectivePortfolio => ({
  supplierId: portfolio.supplierId,
  retailerId: portfolio.retailerId,
  storeId: portfolio.storeId,
  source: toEffectivePortfolioSource(portfolio.source),
  productListIds: portfolio.productListIds,
});
